/**
 * Batch Creation Progress Component
 * 
 * Displays progress during bulk event creation.
 * Shows progress bar, current event, and completion count.
 * 
 * Requirements: 7.1-7.5
 */

import { Card } from '../../common/Card';

export interface BatchProgress {
  current: number;
  total: number;
  currentEventName?: string;
  currentEventDate?: string;
}

export interface BatchCreationProgressProps {
  progress: BatchProgress;
  isVisible: boolean;
}

/**
 * Batch Creation Progress Component
 * Requirements: 7.1-7.5
 */
export function BatchCreationProgress({
  progress,
  isVisible,
}: BatchCreationProgressProps) {
  if (!isVisible) {
    return null;
  }
  
  const percentage = progress.total > 0 
    ? Math.round((progress.current / progress.total) * 100)
    : 0;
  
  return (
    <Card className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Creating Events...
      </h3>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span>{progress.current} of {progress.total} events</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className="bg-primary-500 h-3 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Creating events: ${percentage}% complete`}
          />
        </div>
        <div className="text-center text-sm font-medium text-gray-700 mt-2">
          {percentage}%
        </div>
      </div>
      
      {/* Current Event Info */}
      {progress.currentEventName && (
        <div className="text-sm text-gray-600">
          <p>
            <span className="font-medium">Current event:</span>{' '}
            {progress.currentEventName}
            {progress.currentEventDate && ` on ${progress.currentEventDate}`}
          </p>
        </div>
      )}
    </Card>
  );
}

export default BatchCreationProgress;
